<script setup>
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { usePinnedConversations } from 'dashboard/composables/usePinnedConversations';
import { useSidebarPinnedConversations } from 'dashboard/composables/useSidebarPinnedConversations';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
import { frontendURL, conversationUrl } from 'dashboard/helper/URLHelper';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import Avatar from 'next/avatar/Avatar.vue';
import MessagePreview from './MessagePreview.vue';
import InboxName from '../InboxName.vue';
import ConversationContextMenu from './contextMenu/Index.vue';
import TimeAgo from 'dashboard/components/ui/TimeAgo.vue';
import CardLabels from './conversationCardComponents/CardLabels.vue';
import CardPriorityIcon from 'dashboard/components-next/Conversation/ConversationCard/CardPriorityIcon.vue';
import SLACardLabel from './components/SLACardLabel.vue';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import VoiceCallStatus from './VoiceCallStatus.vue';

const props = defineProps({
  activeLabel: { type: String, default: '' },
  topicId: { type: [String, Number], default: 0 },
  chat: { type: Object, default: () => ({}) },
  hideInboxName: { type: Boolean, default: false },
  hideThumbnail: { type: Boolean, default: false },
  teamId: { type: [String, Number], default: 0 },
  foldersId: { type: [String, Number], default: 0 },
  // MakerHub / Smith3D company inbox-group view ('mk' | 's3'); see Sidebar.vue.
  company: { type: String, default: '' },
  // Active inbox-rail search term — highlights matches in the message preview.
  searchTerm: { type: String, default: '' },
  showAssignee: { type: Boolean, default: false },
  conversationType: { type: String, default: '' },
  selected: { type: Boolean, default: false },
  compact: { type: Boolean, default: false },
  enableContextMenu: { type: Boolean, default: false },
  allowedContextMenuOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'contextMenuToggle',
  'assignAgent',
  'assignLabel',
  'removeLabel',
  'assignTeam',
  'markAsUnread',
  'markAsRead',
  'assignPriority',
  'updateConversationStatus',
  'deleteConversation',
  'selectConversation',
  'deSelectConversation',
]);

const router = useRouter();
const store = useStore();

const hovered = ref(false);
const showContextMenu = ref(false);
const contextMenu = ref({ x: null, y: null });

// Reset UI state when conversation changes at same index (no :key, instance reused on reorder)
// This prevents context menu/hover state from leaking to a different conversation
// Emit contextMenuToggle(false) to sync parent state if menu was open during recycling
const resetState = () => {
  if (showContextMenu.value) {
    emit('contextMenuToggle', false);
  }
  hovered.value = false;
  showContextMenu.value = false;
  contextMenu.value = { x: null, y: null };
};

watch(() => props.chat.id, resetState);

const currentChat = useMapGetter('getSelectedChat');
const inboxesList = useMapGetter('inboxes/getInboxes');
const activeInbox = useMapGetter('getSelectedInbox');
const accountId = useMapGetter('getCurrentAccountId');

const chatMetadata = computed(() => props.chat.meta || {});

const assignee = computed(() => chatMetadata.value.assignee || {});

const senderId = computed(() => chatMetadata.value.sender?.id);

const currentContact = computed(() => {
  const storeContact = senderId.value
    ? store.getters['contacts/getContact'](senderId.value)
    : {};
  if (Object.keys(storeContact).length) return storeContact;
  // Remote search hits (old conversations outside the lazy-loaded window) are
  // never dispatched into the contacts store — their contact only exists on
  // the row's meta.sender. Without this fallback those rows render nameless.
  return chatMetadata.value.sender || {};
});

// MakerHub: show the WhatsApp/marketplace phone number beside a named contact,
// e.g. "Wawa (+60196152589)". Skip it when the contact has no real name or when
// the name already IS the number (unnamed contacts default their name to the phone).
const contactPhone = computed(() => {
  const { name, phone_number: phone } = currentContact.value;
  if (!phone || !name || name === phone) return '';
  // Malaysia (+60xxxxxxxxx) -> local 0xxxxxxxxx; other countries just drop the '+'.
  const local = phone.startsWith('+60')
    ? `0${phone.slice(3)}`
    : phone.replace(/^\+/, '');
  return `(${local})`;
});

const isActiveChat = computed(() => {
  return currentChat.value.id === props.chat.id;
});

const unreadCount = computed(() => props.chat.unread_count);

const hasUnread = computed(() => unreadCount.value > 0);

const isInboxNameVisible = computed(() => !activeInbox.value);

// MakerHub: the AI triage posts its draft / "needs human" output as private notes
// (content_attributes.ai_draft / needs_human), which otherwise clobber the row preview
// because getLastMessage() returns the last non-activity message regardless of author.
// Keep the preview on the last REAL message (incoming customer or human agent reply)
// and surface the AI state as badges (needsHuman / aiCategory) on the channel row instead.
const isAiMessage = message => {
  if (!message) return false;
  const attrs = message.content_attributes || {};
  if (attrs.ai_generated || attrs.ai_draft || attrs.needs_human) return true;
  // mk-wa-agent AI_SAFE_MODE drafts set only the text prefix, no content_attribute
  const content = typeof message.content === 'string' ? message.content : '';
  return (
    content.startsWith('💡 AI Draft') ||
    content.startsWith('🔴 **Needs Human Review**')
  );
};

const lastCustomerMessage = computed(() => {
  const messages = props.chat.messages || [];
  const realMessages = messages.filter(
    message =>
      message.message_type !== 2 && !message.private && !isAiMessage(message)
  );
  if (realMessages.length) {
    // Pick by timestamp, not array position: the websocket ADD_MESSAGE mutation
    // appends in ARRIVAL order, and WhatsApp messages can arrive late carrying
    // their original (older) send time — positional last would pin that stale
    // text as the preview until a reload. Tie-break same-second imports by id.
    return realMessages.reduce((latest, message) => {
      const a = message.created_at || 0;
      const b = latest.created_at || 0;
      if (a > b) return message;
      if (a === b && (message.id || 0) > (latest.id || 0)) return message;
      return latest;
    });
  }
  // The list payload carries only the last message; fall back to the API's
  // last non-activity message (already private:false) before the generic helper.
  return props.chat.last_non_activity_message || getLastMessage(props.chat);
});

// AI badges key off the LAST non-activity message so they reflect the current state:
// a draft/escalation note that is no longer the latest message stops badging once a
// customer or human replies (no stale "Needs Human" on resolved threads).
const lastNonActivityMessage = computed(() => getLastMessage(props.chat));

// Server-computed AI state (Conversation#ai_state) covers cold list loads: the slim
// list payload ships only the literal last message plus a private:false
// last_non_activity_message, so the private triage note never reaches the store
// until the conversation is opened or a websocket event appends it. Once the store
// holds a NEWER non-activity message (customer/human reply, fresh triage note),
// derive from the store instead so badges stay live.
const aiState = computed(() => {
  const state = props.chat.ai_state;
  if (!state) return null;
  const storeMessage = lastNonActivityMessage.value;
  if (storeMessage && storeMessage.created_at > state.timestamp) return null;
  return state;
});

const needsHuman = computed(() => {
  if (aiState.value) return aiState.value.needs_human === true;
  const message = lastNonActivityMessage.value;
  if (!message || !message.private) return false;
  const attrs = message.content_attributes || {};
  if (attrs.needs_human === true) return true;
  return (
    typeof message.content === 'string' &&
    message.content.startsWith('🔴 **Needs Human Review**')
  );
});

// AI has a pending draft reply on this conversation (signals "AI took care of it").
const aiDraft = computed(() => {
  if (aiState.value) return aiState.value.ai_draft === true;
  const message = lastNonActivityMessage.value;
  if (!message || !message.private) return false;
  const attrs = message.content_attributes || {};
  return (
    attrs.ai_draft === true ||
    (typeof message.content === 'string' &&
      message.content.startsWith('💡 AI Draft'))
  );
});

const aiCategory = computed(() => {
  if (!aiDraft.value) return '';
  let intent = '';
  if (aiState.value) {
    intent = aiState.value.intent ? String(aiState.value.intent) : '';
  } else {
    const message = lastNonActivityMessage.value;
    const attrs = message.content_attributes || {};
    // No dedicated category field exists; intent is embedded in the draft header
    // "💡 AI Draft (<intent>, <pct>%)" (content_attributes.intent only on ack notes).
    intent = attrs.intent ? String(attrs.intent) : '';
    if (!intent) {
      const match = (message.content || '').match(/^💡 AI Draft \(([^),]+)/);
      intent = match ? match[1].trim() : '';
    }
  }
  if (!intent) return '';
  return intent.replace(/_/g, ' ').replace(/\b\w/g, char => char.toUpperCase());
});

// MakerHub: sticky AI classification badge (scam / spam) set on
// custom_attributes.mh_badge by the triage (mk-patrol notification rule / mk-agent).
// Rendered as a grey pill; these convs are stamped handled and leave the Urgent tab.
const aiBadge = computed(() => {
  const badge = props.chat.custom_attributes?.mh_badge;
  return badge === 'scam' || badge === 'spam' ? badge : '';
});

// MakerHub: WHY chip — states the reason a row occupies a red queue so staff can
// trust the tabs (committee RCA 2026-07-11). Derived from fields already in the
// list payload; precedence mirrors the tab predicates: the strict/literal pair
// explains reply-state membership, then an overdue owed follow-up, then the
// explicit flag/pin overrides (the only reason left when nothing is owed).
const queueReason = computed(() => {
  const attrs = props.chat.custom_attributes || {};
  if (props.chat.mh_needs_reply === true) {
    // Reply-state WHY chips (CUSTOMER_WAITING / NO_REAL_REPLY) suppressed per
    // owner request (2026-07-12): reply-state rows show no queue-reason chip.
    // FOLLOW_UP_DUE / FLAGGED / PINNED still render on non-reply-state rows.
    return '';
  }
  const cs = attrs.mh_cs || {};
  const handledAt = Number(attrs.mh_handled_at || 0);
  const owedDue =
    cs.owed === true &&
    Number(cs.due || 0) > 0 &&
    Number(cs.due) <= Date.now() / 1000;
  if (owedDue && handledAt < Number(props.chat.last_activity_at || 0)) {
    return 'FOLLOW_UP_DUE';
  }
  if (props.chat.priority === 'urgent') return 'FLAGGED';
  if (attrs.pin_pending === true) return 'PINNED';
  return '';
});

const voiceCallData = computed(() => ({
  status: props.chat.additional_attributes?.call_status,
  direction: props.chat.additional_attributes?.call_direction,
}));

const inboxId = computed(() => props.chat.inbox_id);

const inbox = computed(() => {
  return inboxId.value ? store.getters['inboxes/getInbox'](inboxId.value) : {};
});

const showInboxName = computed(() => {
  return (
    !props.hideInboxName &&
    isInboxNameVisible.value &&
    inboxesList.value.length > 1
  );
});

const showMetaSection = computed(() => {
  return (
    showInboxName.value ||
    (props.showAssignee && assignee.value.name) ||
    props.chat.priority ||
    needsHuman.value ||
    aiDraft.value ||
    !!aiBadge.value ||
    !!queueReason.value
  );
});

const hasSlaPolicyId = computed(() => props.chat?.sla_policy_id);

const showLabelsSection = computed(() => {
  return props.chat.labels?.length > 0 || hasSlaPolicyId.value;
});

const messagePreviewClass = computed(() => {
  return [
    hasUnread.value ? 'font-medium text-n-slate-12' : 'text-n-slate-11',
    !props.compact && hasUnread.value ? 'ltr:pr-4 rtl:pl-4' : '',
    props.compact && hasUnread.value ? 'ltr:pr-6 rtl:pl-6' : '',
  ];
});

const conversationPath = computed(() => {
  return frontendURL(
    conversationUrl({
      accountId: accountId.value,
      activeInbox: activeInbox.value,
      id: props.chat.id,
      label: props.activeLabel,
      topicId: props.topicId,
      teamId: props.teamId,
      conversationType: props.conversationType,
      foldersId: props.foldersId,
      company: props.company,
    }),
    // Search content-hit rows carry the matched message's id — deep-link it so
    // ConversationView opens the thread scrolled to that message (same
    // ?messageId= contract the global search's message results use).
    props.chat.searchMessageId
      ? { messageId: props.chat.searchMessageId }
      : undefined
  );
});

// Jump the ALREADY-OPEN conversation to the search-matched message. Navigating
// again is a no-op (ConversationView's setActiveChat guards on same-id), so
// scroll directly. If the message predates the loaded window, one ranged fetch
// (?after=<target>&before=<oldest loaded>) pulls the gap — MessageFinder's
// messages_between is target-inclusive (id >= after) — then the scroll bus
// event finds its #message<id> element.
const jumpToMatchedMessage = async () => {
  const messageId = Number(props.chat.searchMessageId);
  const { id: conversationId, messages = [] } = currentChat.value || {};
  const isLoaded = messages.some(m => Number(m.id) === messageId);
  if (!isLoaded && messages.length && Number(messages[0].id) > messageId) {
    await store.dispatch('fetchPreviousMessages', {
      conversationId,
      after: messageId,
      before: messages[0].id,
    });
  }
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, { messageId });
};

const onCardClick = e => {
  const path = conversationPath.value;
  if (!path) return;

  // Handle Ctrl/Cmd + Click for new tab
  if (e.metaKey || e.ctrlKey) {
    e.preventDefault();
    window.open(
      `${window.chatwootConfig.hostURL}${path}`,
      '_blank',
      'noopener,noreferrer'
    );
    return;
  }

  // Skip if already active — unless this is a search content hit, which should
  // still scroll the open thread to the matched message.
  if (isActiveChat.value) {
    if (props.chat.searchMessageId) jumpToMatchedMessage();
    return;
  }

  // STRING form, not { path }: vue-router's object form does not parse a query
  // string embedded in `path`, so ?messageId= was silently dropped (caught on
  // stage — navigation landed without the deep link).
  router.push(path);
};

const onThumbnailHover = () => {
  hovered.value = !props.hideThumbnail;
};

const onThumbnailLeave = () => {
  hovered.value = false;
};

const onSelectConversation = checked => {
  if (checked) {
    emit('selectConversation', props.chat.id, inbox.value.id);
  } else {
    emit('deSelectConversation', props.chat.id, inbox.value.id);
  }
};

const openContextMenu = e => {
  if (!props.enableContextMenu) return;
  e.preventDefault();
  emit('contextMenuToggle', true);
  contextMenu.value.x = e.pageX || e.clientX;
  contextMenu.value.y = e.pageY || e.clientY;
  showContextMenu.value = true;
};

const closeContextMenu = () => {
  emit('contextMenuToggle', false);
  showContextMenu.value = false;
  contextMenu.value.x = null;
  contextMenu.value.y = null;
};

const onUpdateConversation = (status, snoozedUntil) => {
  closeContextMenu();
  emit('updateConversationStatus', props.chat.id, status, snoozedUntil);
};

const onAssignAgent = agent => {
  emit('assignAgent', agent, [props.chat.id]);
  closeContextMenu();
};

const onAssignLabel = label => {
  emit('assignLabel', [label.title], [props.chat.id]);
};

const onRemoveLabel = label => {
  emit('removeLabel', [label.title], [props.chat.id]);
};

const onAssignTeam = team => {
  emit('assignTeam', team, props.chat.id);
  closeContextMenu();
};

// WhatsApp-style pin (per-browser, localStorage). Pin state is reactive and
// shared with the list sort in ChatList so pinned chats float to the top.
const currentAccountId = useMapGetter('getCurrentAccountId');
const { isPinned: isPinnedFn, togglePin } = usePinnedConversations(
  currentAccountId.value
);
const isPinned = computed(() => isPinnedFn(props.chat.id));
const pinEmoji = '📌';
const onTogglePin = () => {
  togglePin(props.chat.id, props.chat);
  closeContextMenu();
};

// "Pin to left menu" — per-user sidebar shortcut (server-synced), a separate
// feature from the list pin above.
const { isPinned: isSidebarPinnedFn, toggle: toggleSidebarPin } =
  useSidebarPinnedConversations();
const isSidebarPinned = computed(() => isSidebarPinnedFn(props.chat.id));
const onToggleSidebarPin = () => {
  toggleSidebarPin(props.chat);
  closeContextMenu();
};

const markAsUnread = () => {
  emit('markAsUnread', props.chat.id);
  closeContextMenu();
};

const markAsRead = () => {
  emit('markAsRead', props.chat.id);
  closeContextMenu();
};

const assignPriority = priority => {
  emit('assignPriority', priority, props.chat.id);
  closeContextMenu();
};

// MakerHub: "Resolve" → mark handled. Clears the conversation from the Urgent (and
// future Pending) tab without a reply via a merge-safe server endpoint; status stays
// open so it remains in All, and a newer customer message re-urgents the card.
const markHandled = () => {
  store.dispatch('markConversationHandled', props.chat.id);
  closeContextMenu();
};

// MakerHub: manual queue placement from the right-click menu — the human
// counterpart of the AI patrol's levers. The store actions mark-handle first
// (clearing the current red-tab membership) then pin the conversation to the
// Urgent (priority=urgent) or Pending (pin_pending) tab.
const { t } = useI18n();

const setUrgent = () => {
  store
    .dispatch('markConversationUrgent', props.chat.id)
    .then(() => useAlert(t('CONVERSATION.MARK_URGENT_SUCCESS')))
    .catch(() => {});
  closeContextMenu();
};

const setPending = () => {
  store
    .dispatch('markConversationPending', props.chat.id)
    .then(() => useAlert(t('CONVERSATION.MARK_PENDING_SUCCESS')))
    .catch(() => {});
  closeContextMenu();
};

const deleteConversation = () => {
  emit('deleteConversation', props.chat.id);
  closeContextMenu();
};
</script>

<template>
  <div
    class="relative flex items-start flex-grow-0 flex-shrink-0 w-auto max-w-full py-0 border-t-0 border-b-0 border-l-0 border-r-0 border-transparent border-solid cursor-pointer conversation hover:bg-n-alpha-1 dark:hover:bg-n-alpha-3 group"
    :class="{
      'active animate-card-select bg-n-background border-n-weak': isActiveChat,
      'bg-n-slate-2': selected,
      'px-0': compact,
      'px-3': !compact,
    }"
    @click="onCardClick"
    @contextmenu="openContextMenu($event)"
  >
    <div
      class="relative"
      @mouseenter="onThumbnailHover"
      @mouseleave="onThumbnailLeave"
    >
      <Avatar
        v-if="!hideThumbnail"
        :name="currentContact.name"
        :src="currentContact.thumbnail"
        :size="32"
        :status="currentContact.availability_status"
        :class="!showInboxName ? 'mt-4' : 'mt-8'"
        hide-offline-status
        rounded-full
      >
        <template #overlay="{ size }">
          <label
            v-if="hovered || selected"
            class="flex items-center justify-center rounded-full cursor-pointer absolute inset-0 z-10 backdrop-blur-[2px]"
            :style="{ width: `${size}px`, height: `${size}px` }"
            @click.stop
          >
            <input
              :value="selected"
              :checked="selected"
              class="!m-0 cursor-pointer"
              type="checkbox"
              @change="onSelectConversation($event.target.checked)"
            />
          </label>
        </template>
      </Avatar>
    </div>
    <div
      class="px-0 py-3 border-b group-hover:border-transparent flex-1 border-n-slate-3 min-w-0"
    >
      <div
        v-if="showMetaSection"
        class="flex items-center min-w-0 gap-1"
        :class="{
          'ltr:ml-2 rtl:mr-2': !compact,
          'mx-2': compact,
        }"
      >
        <InboxName v-if="showInboxName" :inbox="inbox" class="flex-1 min-w-0" />
        <div
          class="flex items-baseline gap-2 flex-shrink-0"
          :class="{
            'flex-1 justify-between': !showInboxName,
          }"
        >
          <span
            v-if="showAssignee && assignee.name"
            class="text-n-slate-11 text-xs font-medium leading-3 py-0.5 px-0 inline-flex items-center truncate"
          >
            <fluent-icon icon="person" size="12" class="text-n-slate-11" />
            {{ assignee.name }}
          </span>
          <CardPriorityIcon
            :priority="chat.priority"
            class="flex-shrink-0 !size-3.5"
          />
        </div>
        <span
          v-if="needsHuman"
          class="flex-shrink-0 ltr:ml-auto rtl:mr-auto rounded-full text-xxs font-semibold leading-4 px-1.5 py-0 text-white bg-n-ruby-9"
        >
          {{ $t('CHAT_LIST.AI_BADGES.NEEDS_HUMAN') }}
        </span>
        <span
          v-if="aiDraft"
          class="flex-shrink-0 inline-flex items-center overflow-hidden rounded-full text-xxs font-semibold leading-4"
          :class="{ 'ltr:ml-auto rtl:mr-auto': !needsHuman }"
        >
          <span class="px-1.5 py-0 text-white bg-n-iris-9">
            {{ $t('CHAT_LIST.AI_BADGES.AI') }}
          </span>
          <span class="px-1.5 py-0 capitalize text-n-slate-12 bg-n-slate-3">
            {{ aiCategory || $t('CHAT_LIST.AI_BADGES.DRAFT') }}
          </span>
        </span>
        <span
          v-if="aiBadge"
          class="flex-shrink-0 rounded-full text-xxs font-semibold leading-4 px-1.5 py-0 text-white bg-n-slate-9"
          :class="{ 'ltr:ml-auto rtl:mr-auto': !needsHuman && !aiDraft }"
        >
          {{
            aiBadge === 'scam'
              ? $t('CHAT_LIST.AI_BADGES.SCAM')
              : $t('CHAT_LIST.AI_BADGES.SPAM')
          }}
        </span>
        <span
          v-if="queueReason"
          class="flex-shrink-0 rounded-full text-xxs font-medium leading-4 px-1.5 py-0 bg-n-alpha-2 text-n-slate-11"
          :class="{
            'ltr:ml-auto rtl:mr-auto': !needsHuman && !aiDraft && !aiBadge,
          }"
        >
          {{ $t(`CHAT_LIST.QUEUE_REASON.${queueReason}`) }}
        </span>
      </div>
      <h4
        class="conversation--user text-sm my-0 mx-2 capitalize pt-0.5 text-ellipsis overflow-hidden whitespace-nowrap flex-1 min-w-0 ltr:pr-16 rtl:pl-16 text-n-slate-12"
        :class="hasUnread ? 'font-semibold' : 'font-medium'"
      >
        <span v-if="isPinned" class="ltr:mr-1 rtl:ml-1">{{ pinEmoji }}</span>
        {{ currentContact.name }}
        <span
          v-if="contactPhone"
          class="font-normal normal-case text-xs text-n-slate-11"
        >
          {{ contactPhone }}
        </span>
      </h4>
      <VoiceCallStatus
        v-if="voiceCallData.status"
        key="voice-status-row"
        :status="voiceCallData.status"
        :direction="voiceCallData.direction"
        :message-preview-class="messagePreviewClass"
      />
      <MessagePreview
        v-else-if="lastCustomerMessage"
        key="message-preview"
        :message="lastCustomerMessage"
        :highlight-term="searchTerm"
        class="my-0 mx-2 leading-6 h-6 flex-1 min-w-0 text-sm"
        :class="messagePreviewClass"
      />
      <p
        v-else
        key="no-messages"
        class="text-n-slate-11 text-sm my-0 mx-2 leading-6 h-6 flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap"
        :class="messagePreviewClass"
      >
        <fluent-icon
          size="16"
          class="-mt-0.5 align-middle inline-block text-n-slate-10"
          icon="info"
        />
        <span class="mx-0.5">
          {{ $t(`CHAT_LIST.NO_MESSAGES`) }}
        </span>
      </p>
      <div
        class="absolute flex flex-col ltr:right-3 rtl:left-3"
        :class="showMetaSection ? 'top-8' : 'top-4'"
      >
        <span class="ml-auto font-normal leading-4 text-xxs">
          <TimeAgo
            :last-activity-timestamp="chat.timestamp"
            :created-at-timestamp="chat.created_at"
            :conversation-id="chat.id"
          />
        </span>
        <span
          class="shadow-lg rounded-full text-xxs font-semibold h-4 leading-4 ltr:ml-auto rtl:mr-auto mt-1 min-w-[1rem] px-1 py-0 text-center text-white bg-n-teal-9"
          :class="hasUnread ? 'block' : 'hidden'"
        >
          {{ unreadCount > 9 ? '9+' : unreadCount }}
        </span>
      </div>
      <CardLabels
        v-if="showLabelsSection"
        :conversation-labels="chat.labels"
        class="mt-0.5 mx-2 mb-0"
      >
        <template v-if="hasSlaPolicyId" #before>
          <SLACardLabel :chat="chat" class="ltr:mr-1 rtl:ml-1" />
        </template>
      </CardLabels>
    </div>
    <ContextMenu
      v-if="showContextMenu"
      :x="contextMenu.x"
      :y="contextMenu.y"
      @close="closeContextMenu"
    >
      <ConversationContextMenu
        :status="chat.status"
        :inbox-id="inbox.id"
        :priority="chat.priority"
        :chat-id="chat.id"
        :has-unread-messages="hasUnread"
        :is-pinned="isPinned"
        :is-sidebar-pinned="isSidebarPinned"
        :conversation-labels="chat.labels"
        :conversation-url="conversationPath"
        :allowed-options="allowedContextMenuOptions"
        @toggle-pin="onTogglePin"
        @toggle-sidebar-pin="onToggleSidebarPin"
        @update-conversation="onUpdateConversation"
        @assign-agent="onAssignAgent"
        @assign-label="onAssignLabel"
        @remove-label="onRemoveLabel"
        @assign-team="onAssignTeam"
        @mark-as-unread="markAsUnread"
        @mark-as-read="markAsRead"
        @mark-handled="markHandled"
        @set-urgent="setUrgent"
        @set-pending="setPending"
        @assign-priority="assignPriority"
        @delete-conversation="deleteConversation"
        @close="closeContextMenu"
      />
    </ContextMenu>
  </div>
</template>
